import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, ArrowRight, CheckCircle2, MessageCircle, Paperclip } from "lucide-react";
import { QUOTE } from "@/constants/testIds";
import { SERVICES, EXTRA_SERVICES, buildQuoteWhatsAppMessage, waLink } from "@/config/brand";
import { api } from "@/lib/api";

const PROPERTY_TYPES = ["Flat", "House", "Studio", "Office", "Storage unit", "Other"];
const allServices = [...SERVICES.map((s) => s.title), ...EXTRA_SERVICES.map((s) => s.title)];

const STEPS = [
    "Service",
    "Collection",
    "Delivery",
    "Inventory",
    "Access & extras",
    "Your details",
    "Review & submit",
];

export const QuoteForm = ({ defaultService = "House Removals", onSuccess }) => {
    const nav = useNavigate();
    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [files, setFiles] = useState([]);

    const [values, setValues] = useState({
        service: defaultService,
        moving_date: "",
        flexible_date: false,
        collection_address: "", collection_postcode: "", collection_property_type: "Flat", collection_floor: "Ground", collection_lift: false,
        delivery_address: "", delivery_postcode: "", delivery_property_type: "Flat", delivery_floor: "Ground", delivery_lift: false,
        bedrooms: "1", boxes: "10", large_items: "", dismantling_required: false, packing_assistance: false,
        parking_info: "", additional_details: "",
        full_name: "", phone: "", email: "", preferred_contact: "Any",
        consent: false, website: "",
    });
    const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e?.target ? e.target.value : e }));

    const progress = useMemo(() => Math.round(((step + 1) / STEPS.length) * 100), [step]);

    const validateStep = () => {
        if (step === 0 && !values.service) return "Please select a service.";
        if (step === 5) {
            if (!values.full_name || values.full_name.length < 2) return "Please enter your name.";
            if (!values.phone) return "Please provide a phone number.";
            if (!values.email) return "Please provide an email address.";
        }
        if (step === 6 && !values.consent) return "Please confirm you’re happy for us to contact you.";
        return null;
    };

    const next = () => {
        const err = validateStep();
        if (err) return toast.error(err);
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
    };
    const back = () => setStep((s) => Math.max(s - 1, 0));

    const submit = () => {
        const err = validateStep();
        if (err) return toast.error(err);
        setBusy(true);
        try {
            const message = buildQuoteWhatsAppMessage(values);
            const reference = `WA-${Date.now().toString().slice(-6)}`;
            const whatsapp_followup_message = message;
            setResult({ reference, whatsapp_followup_message });
            toast.success("Opening WhatsApp — press Send to complete your enquiry.");
            window.open(waLink(message), "_blank", "noopener,noreferrer");
            onSuccess?.({ reference, whatsapp_followup_message });
        } finally {
            setBusy(false);
        }
    };

    if (result) {
        return (
            <div className="rounded-2xl bg-white border border-[#17252D]/10 p-6 sm:p-8 text-center">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#001B3D]/10 text-[#001B3D]">
                    <CheckCircle2 className="h-7 w-7" />
                </span>
                <h2 className="mt-4 text-2xl font-semibold">WhatsApp opened — press Send to complete</h2>
                <p className="mt-2 text-[#17252D]/75">
                    We opened WhatsApp with your details pre-filled to <strong>{" +44 7883 508077"}</strong>.
                    Just press <strong>Send</strong> in WhatsApp to submit your enquiry.
                    If the WhatsApp window didn’t open, use the button below.
                </p>
                <p className="mt-3 text-sm text-[#17252D]/60">
                    Reference: <strong data-testid={QUOTE.successRef}>{result.reference}</strong>
                </p>
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                        data-testid={QUOTE.successWhatsapp}
                        href={waLink(result.whatsapp_followup_message)}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <Button className="bg-[#25D366] hover:bg-[#20b955] text-white font-semibold h-11 px-6">
                            <MessageCircle className="h-4 w-4 mr-2" /> Open WhatsApp again
                        </Button>
                    </a>
                    <Button variant="outline" onClick={() => nav("/")}>Back to home</Button>
                </div>
            </div>
        );
    }

    return (
        <form
            data-testid={QUOTE.form}
            onSubmit={(e) => { e.preventDefault(); step === STEPS.length - 1 ? submit() : next(); }}
            className="rounded-2xl bg-white border border-[#17252D]/10 p-5 sm:p-8"
        >
            <div className="flex items-center justify-between text-sm text-[#17252D]/70">
                <div data-testid={QUOTE.stepIndicator}>Step {step + 1} of {STEPS.length} — {STEPS[step]}</div>
                <div className="tabular-nums">{progress}%</div>
            </div>
            <Progress value={progress} className="mt-2 h-2" />

            <div className="mt-6 space-y-4">
                {step === 0 && (
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                            <Label>Service required</Label>
                            <Select value={values.service} onValueChange={(v) => set("service")(v)}>
                                <SelectTrigger data-testid="quote-select-service"><SelectValue /></SelectTrigger>
                                <SelectContent>{allServices.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Preferred moving date</Label>
                            <Input type="date" data-testid="quote-input-date" value={values.moving_date} onChange={set("moving_date")} />
                        </div>
                        <div className="flex items-center gap-2 pt-6">
                            <Checkbox id="flex" checked={values.flexible_date} onCheckedChange={(v) => set("flexible_date")(!!v)} />
                            <Label htmlFor="flex">My date is flexible</Label>
                        </div>
                    </div>
                )}

                {step === 1 && (
                    <AddressBlock prefix="collection" values={values} set={set} label="Collection" />
                )}
                {step === 2 && (
                    <AddressBlock prefix="delivery" values={values} set={set} label="Delivery" />
                )}

                {step === 3 && (
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <Label>Number of bedrooms</Label>
                            <Select value={values.bedrooms} onValueChange={(v) => set("bedrooms")(v)}>
                                <SelectTrigger data-testid="quote-input-bedrooms"><SelectValue /></SelectTrigger>
                                <SelectContent>{["Studio", "1", "2", "3", "4", "5+"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Approx. number of boxes</Label>
                            <Input data-testid="quote-input-boxes" value={values.boxes} onChange={set("boxes")} placeholder="e.g. 20" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Large or heavy items</Label>
                            <Textarea data-testid="quote-input-large" value={values.large_items} onChange={set("large_items")} placeholder="Sofas, wardrobes, appliances, pianos…" />
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox id="dismantle" checked={values.dismantling_required} onCheckedChange={(v) => set("dismantling_required")(!!v)} />
                            <Label htmlFor="dismantle">Dismantling / reassembly needed</Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox id="packing" checked={values.packing_assistance} onCheckedChange={(v) => set("packing_assistance")(!!v)} />
                            <Label htmlFor="packing">Packing assistance required</Label>
                        </div>
                    </div>
                )}

                {step === 4 && (
                    <div className="grid gap-4">
                        <div>
                            <Label>Parking / access notes</Label>
                            <Textarea data-testid="quote-input-parking" value={values.parking_info} onChange={set("parking_info")} placeholder="Restrictions, permits, narrow roads, stairs, lifts…" />
                        </div>
                        <div>
                            <Label>Anything else we should know?</Label>
                            <Textarea data-testid="quote-input-additional" value={values.additional_details} onChange={set("additional_details")} placeholder="Anything else that could affect your move" />
                        </div>
                        <div>
                            <Label>Optional: attach photos of your items or access</Label>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                                <label className="inline-flex items-center gap-2 rounded-md border border-[#17252D]/15 px-3 py-2 text-sm cursor-pointer hover:bg-[#F3F7F9]">
                                    <Paperclip className="h-4 w-4" />
                                    <span>Choose files</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 6))}
                                        className="hidden"
                                        data-testid="quote-input-files"
                                    />
                                </label>
                                {files.length > 0 && (
                                    <span className="text-xs text-[#17252D]/70">{files.length} file(s) selected</span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {step === 5 && (
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                            <Label>Full name</Label>
                            <Input required data-testid="quote-input-name" value={values.full_name} onChange={set("full_name")} />
                        </div>
                        <div>
                            <Label>Phone</Label>
                            <Input required data-testid="quote-input-phone" value={values.phone} onChange={set("phone")} />
                        </div>
                        <div>
                            <Label>Email</Label>
                            <Input required type="email" data-testid="quote-input-email" value={values.email} onChange={set("email")} />
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Preferred contact method</Label>
                            <RadioGroup value={values.preferred_contact} onValueChange={(v) => set("preferred_contact")(v)} className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                                {["Any", "Phone", "WhatsApp", "Email"].map((m) => (
                                    <label key={m} className="flex items-center gap-2 rounded-md border border-[#17252D]/15 px-3 py-2 cursor-pointer hover:bg-[#F3F7F9]">
                                        <RadioGroupItem value={m} data-testid={`quote-radio-contact-${m.toLowerCase()}`} />
                                        {m}
                                    </label>
                                ))}
                            </RadioGroup>
                        </div>
                    </div>
                )}

                {step === 6 && (
                    <div className="grid gap-4">
                        <div className="rounded-xl bg-[#F3F7F9] border border-[#17252D]/10 p-4 text-sm space-y-1">
                            <div><strong>Service:</strong> {values.service}</div>
                            <div><strong>Date:</strong> {values.moving_date || "Flexible"}</div>
                            <div><strong>From:</strong> {values.collection_postcode || values.collection_address || "—"}</div>
                            <div><strong>To:</strong> {values.delivery_postcode || values.delivery_address || "—"}</div>
                            <div><strong>Bedrooms:</strong> {values.bedrooms} • <strong>Boxes:</strong> {values.boxes || "—"}</div>
                            <div><strong>Contact:</strong> {values.full_name}, {values.phone}, {values.email}</div>
                        </div>
                        <label className="flex items-start gap-3 text-sm">
                            <Checkbox
                                checked={values.consent}
                                onCheckedChange={(v) => set("consent")(!!v)}
                                className="mt-0.5"
                                data-testid="quote-checkbox-consent"
                            />
                            <span>I’m happy for YS Removals to contact me about my quote request. See our <a className="underline" href="/privacy-policy">privacy policy</a>.</span>
                        </label>
                    </div>
                )}
            </div>

            {/* Honeypot */}
            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={values.website} onChange={set("website")} className="hidden" aria-hidden="true" />

            <div className="mt-8 flex items-center justify-between gap-3">
                <Button type="button" variant="outline" onClick={back} disabled={step === 0} data-testid={QUOTE.back}>
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                {step < STEPS.length - 1 ? (
                    <Button type="submit" className="bg-[#001B3D] hover:bg-[#001B3D]/90 text-white" data-testid={QUOTE.next}>
                        Next <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                ) : (
                    <Button type="submit" disabled={busy} className="bg-[#25D366] hover:bg-[#20b955] text-white" data-testid={QUOTE.submit}>
                        <MessageCircle className="h-4 w-4 mr-2" /> {busy ? "Opening WhatsApp…" : "Send quote on WhatsApp"}
                    </Button>
                )}
            </div>
        </form>
    );
};

function AddressBlock({ prefix, values, set, label }) {
    return (
        <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
                <Label>{label} address</Label>
                <Input data-testid={`quote-input-${prefix}-address`} value={values[`${prefix}_address`]} onChange={set(`${prefix}_address`)} placeholder="Street, area" />
            </div>
            <div>
                <Label>Postcode</Label>
                <Input data-testid={`quote-input-${prefix}-postcode`} value={values[`${prefix}_postcode`]} onChange={set(`${prefix}_postcode`)} placeholder="e.g. E1 6AN" />
            </div>
            <div>
                <Label>Property type</Label>
                <Select value={values[`${prefix}_property_type`]} onValueChange={(v) => set(`${prefix}_property_type`)(v)}>
                    <SelectTrigger data-testid={`quote-input-${prefix}-type`}><SelectValue /></SelectTrigger>
                    <SelectContent>{PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
            </div>
            <div>
                <Label>Floor</Label>
                <Select value={values[`${prefix}_floor`]} onValueChange={(v) => set(`${prefix}_floor`)(v)}>
                    <SelectTrigger data-testid={`quote-input-${prefix}-floor`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["Basement", "Ground", "1st", "2nd", "3rd", "4th", "5th+"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
            </div>
            <div className="flex items-center gap-2 pt-6">
                <Checkbox id={`${prefix}-lift`} checked={values[`${prefix}_lift`]} onCheckedChange={(v) => set(`${prefix}_lift`)(!!v)} />
                <Label htmlFor={`${prefix}-lift`}>Lift available</Label>
            </div>
        </div>
    );
}
